import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Typography,
  Box,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import { ReactElement, useState } from "react";

import { TenantEnableSubaccountsMutation } from "~frontend/__generated__/graphql";
import { TENANT_ENABLE_SUBACCOUNTS } from "~frontend/graphql/tenant";
import { useNonCacheClearingApolloClient } from "~frontend/providers/NonCacheClearingApolloClientProvider";
import useMutation from "~frontend/util/useMutation";

export default function OrgSubaccountsEnable(): ReactElement {
  const snackbar = useSnackbar();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const client = useNonCacheClearingApolloClient();

  const [enableSubaccounts] = useMutation<TenantEnableSubaccountsMutation>(
    TENANT_ENABLE_SUBACCOUNTS,
    {
      onErrorSnackbarMessage: `Failed to enable subaccounts.`,
      client,
    }
  );

  async function handleEnable() {
    const { errors } = await enableSubaccounts();

    if (!errors) {
      snackbar.enqueueSnackbar("Subaccounts enabled.", { variant: "info" });
    }
  }

  async function handleClick() {
    const { confirmed } = await confirm({
      title: "Are you sure?",
      description:
        "Are you sure you want to enable subaccounts? You can't undo this action.",
    });
    if (!confirmed) {
      return;
    }

    setLoading(true);
    await handleEnable();
    setLoading(false);
  }

  return (
    <>
      <Card sx={{ maxWidth: 800 }}>
        <CardHeader
          sx={{ p: 2 }}
          title={<Typography variant="h2">Enable Subaccounts</Typography>}
        />
        <CardContent>
          <p>
            If your Scale to Win Text account will be used to text for multiple
            organizations (e.g., candidates, chapters, etc.), you can separate
            their traffic using the Subaccounts feature. Subaccounts allow you
            to organize and manage distinct teams or organizations more
            effectively.
          </p>
          <p>Each subaccount will have its own:</p>
          <ul>
            <li>Campaigns</li>
            <li>Settings</li>
            <li>Integrations</li>
            <li>Strike Lists</li>
            <li>Users</li>
          </ul>
          <p>
            <strong>Access Controls:</strong> Organization Owners will
            automatically have access to all current and future subaccounts
            within this organization. Additionally, Organization Owners can
            create new subaccounts and assign subaccount owners as needed.
          </p>
          <p>
            <strong>Billing Information</strong>: Subaccounts are billed as part
            of the parent account, and all billing-related settings, such as
            payment methods and invoices, are managed centrally. Organization
            Owners can review their billing details and invoices through the
            billing portal. Please read{" "}
            <a href="https://support.scaletowin.com/billing-for-teams">
              this guide
            </a>{" "}
            to learn more about billing for subaccounts and teams.
          </p>
          <p>
            <strong>Default Opt-Out and Strike List Policies:</strong>
          </p>

          <ul>
            <li>
              <strong>Opt-Outs:</strong> Permanent Opt-Outs are shared across
              all subaccounts in your organization, ensuring compliance and
              consistent communication practices.
            </li>
            <li>
              <strong>Strike Lists:</strong> Temporary strike lists are unique
              to each subaccount and are not shared, providing flexibility for
              targeted messaging strategies.
            </li>
          </ul>

          <p>
            If you want to disable shared Opt-Outs across subaccounts, please{" "}
            <a href="https://support.scaletowin.com/contact">
              contact Scale to Win Support
            </a>
            .
          </p>

          <Box sx={{ mt: 4 }}>
            <Button
              onClick={handleClick}
              disabled={loading}
              color="primary"
              variant="contained"
            >
              {loading ? "Enabling..." : "Enable Subaccounts"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </>
  );
}
